import React, { useState, useCallback, useEffect } from 'react';
import DigitSlot from '../DigitSlot';
import './DigitSlotMachine.css';
import SoundEffects from '../../SoundEffects';

export type DigitStopSoundType = 'ding' | 'pop' | 'click' | 'chime';

interface DigitSlotMachineProps {
  value: string;
  isSpinning: boolean;
  onAllAnimationsComplete?: () => void;
  spinDurationPerDigit?: number;
  delayBetweenDigits?: number;
  soundEffects?: SoundEffects | null;
  digitStopSound?: DigitStopSoundType;
  skipToResult?: boolean;
}

const DigitSlotMachine: React.FC<DigitSlotMachineProps> = ({
  value,
  isSpinning,
  onAllAnimationsComplete,
  spinDurationPerDigit = 1500,
  delayBetweenDigits = 500,
  soundEffects,
  digitStopSound = 'ding',
  skipToResult = false,
}) => {
  const [activeDigitIndex, setActiveDigitIndex] = useState<number>(-1);
  const [completedDigits, setCompletedDigits] = useState<Set<number>>(new Set());
  const [skipped, setSkipped] = useState<boolean>(false);
  const digits = value.split('');

  // Reset state when spinning starts
  useEffect(() => {
    if (isSpinning) {
      setActiveDigitIndex(0);
      setCompletedDigits(new Set());
      setSkipped(false);
    } else {
      setActiveDigitIndex(-1);
    }
  }, [isSpinning]);

  // Play spin sound when active digit changes (new digit starts spinning)
  useEffect(() => {
    if (activeDigitIndex >= 0 && isSpinning && !skipped && soundEffects) {
      // Play spin sound for this digit's duration
      // The spin sound will stop naturally when it reaches the duration
      // Don't call stopAll() here as it would cut off the "ding" sound from the previous digit
      const spinDurationInSeconds = spinDurationPerDigit / 1000;
      soundEffects.spin(spinDurationInSeconds);
    }
  }, [activeDigitIndex, isSpinning, skipped, soundEffects, spinDurationPerDigit]);

  // Handle skip - immediately show all results
  useEffect(() => {
    if (skipToResult && isSpinning && !skipped) {
      setSkipped(true);
      // Stop all currently playing sounds
      if (soundEffects) {
        soundEffects.stopAll();
      }
      // Mark all digits as completed
      const allDigits = new Set(digits.map((_, i) => i));
      setCompletedDigits(allDigits);
      setActiveDigitIndex(-1);
      // Play final sound
      if (soundEffects) {
        soundEffects.digitStop(digitStopSound);
      }
      // Trigger completion callback
      onAllAnimationsComplete?.();
    }
  }, [
    skipToResult,
    isSpinning,
    skipped,
    digits,
    soundEffects,
    digitStopSound,
    onAllAnimationsComplete,
  ]);

  // Handle when a digit's animation completes
  const handleDigitAnimationEnd = useCallback(
    (index: number) => {
      // Play digit stop sound
      if (soundEffects) {
        soundEffects.digitStop(digitStopSound);
      }

      setCompletedDigits((prev) => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });

      // Move to next digit
      const nextIndex = index + 1;
      if (nextIndex < digits.length) {
        setActiveDigitIndex(nextIndex);
      } else {
        // All digits completed
        setActiveDigitIndex(-1);
        onAllAnimationsComplete?.();
      }
    },
    [digits.length, onAllAnimationsComplete, soundEffects, digitStopSound]
  );

  // Calculate if a digit should be spinning
  const isDigitSpinning = useCallback(
    (index: number): boolean => {
      if (!isSpinning) return false;
      return index === activeDigitIndex;
    },
    [isSpinning, activeDigitIndex]
  );

  // Calculate delay for each digit (0 for all since we control sequencing via activeDigitIndex)
  const getDelay = useCallback((_index: number): number => {
    return 0; // No additional delay, sequencing handled by activeDigitIndex
  }, []);

  // Check if a digit should show placeholder "X"
  // Show placeholder if: spinning is active AND digit hasn't started yet AND digit hasn't completed
  const shouldShowPlaceholder = useCallback(
    (index: number): boolean => {
      if (!isSpinning) return false;
      // Show X if this digit hasn't started spinning yet and hasn't completed
      return index > activeDigitIndex && !completedDigits.has(index);
    },
    [isSpinning, activeDigitIndex, completedDigits]
  );

  return (
    <div className="digit-slot-machine">
      {digits.map((digit, index) => (
        <DigitSlot
          key={`digit-${index}`}
          targetDigit={digit}
          isSpinning={isDigitSpinning(index)}
          onAnimationEnd={() => handleDigitAnimationEnd(index)}
          delay={getDelay(index)}
          spinDuration={spinDurationPerDigit}
          showPlaceholder={shouldShowPlaceholder(index)}
        />
      ))}
    </div>
  );
};

export default DigitSlotMachine;
